export interface LowLightDetectorOptions {
  /** Brightness threshold below which a frame is considered dark (0 - 255). Default: 50. */
  lowThreshold?: number;
  /** Brightness threshold above which a frame is considered adequately lit (0 - 255). Default: 65. */
  normalThreshold?: number;
  /** Consecutive low-light observations required before triggering low-light status. Default: 3. */
  consecutiveRequired?: number;
  /** Consecutive normal-light observations required before clearing low-light status. Default: 2. */
  consecutiveNormalRequired?: number;
}

/**
 * Calculates the average perceived brightness (0 - 255) of an RGBA pixel buffer.
 * Uses ITU-R BT.601 luma formula: Y = 0.299 * R + 0.587 * G + 0.114 * B.
 */
export function calculateAverageBrightness(data: Uint8ClampedArray | number[]): number {
  if (!data || data.length === 0) return 0;
  const pixelCount = Math.floor(data.length / 4);
  if (pixelCount === 0) return 0;

  let totalLuma = 0;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    totalLuma += 0.299 * r + 0.587 * g + 0.114 * b;
  }

  return totalLuma / pixelCount;
}

/**
 * Samples a frame from an HTMLVideoElement onto a small canvas to measure its average brightness.
 * Returns null if the video is not ready or has zero dimensions.
 */
export function sampleVideoBrightness(
  video: HTMLVideoElement | null,
  canvas?: HTMLCanvasElement,
  sampleWidth = 32,
  sampleHeight = 32
): number | null {
  if (!video) return null;
  if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
    return null;
  }

  try {
    const cvs = canvas || document.createElement("canvas");
    if (cvs.width !== sampleWidth) cvs.width = sampleWidth;
    if (cvs.height !== sampleHeight) cvs.height = sampleHeight;

    const ctx = cvs.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0, sampleWidth, sampleHeight);
    const imageData = ctx.getImageData(0, 0, sampleWidth, sampleHeight);
    return calculateAverageBrightness(imageData.data);
  } catch {
    return null;
  }
}

/**
 * Detects persistently low lighting conditions with hysteresis to avoid flickering.
 */
export class LowLightDetector {
  private lowCount = 0;
  private normalCount = 0;
  private isLow = false;
  private readonly lowThreshold: number;
  private readonly normalThreshold: number;
  private readonly consecutiveRequired: number;
  private readonly consecutiveNormalRequired: number;

  constructor(opts?: LowLightDetectorOptions) {
    this.lowThreshold = opts?.lowThreshold ?? 50;
    this.normalThreshold = opts?.normalThreshold ?? 65;
    this.consecutiveRequired = opts?.consecutiveRequired ?? 3;
    this.consecutiveNormalRequired = opts?.consecutiveNormalRequired ?? 2;
  }

  /**
   * Updates the detector with a new brightness reading (0 - 255) and returns current low-light state.
   */
  public update(brightness: number): boolean {
    if (brightness < this.lowThreshold) {
      this.normalCount = 0;
      this.lowCount++;
      if (this.lowCount >= this.consecutiveRequired) {
        this.isLow = true;
      }
    } else if (brightness >= this.normalThreshold) {
      this.lowCount = 0;
      this.normalCount++;
      if (this.normalCount >= this.consecutiveNormalRequired) {
        this.isLow = false;
      }
    } else {
      // In hysteresis deadband between lowThreshold and normalThreshold:
      // Reset streak counts but retain current state to avoid flickering.
      this.lowCount = 0;
      this.normalCount = 0;
    }
    return this.isLow;
  }

  public isLowLight(): boolean {
    return this.isLow;
  }

  public reset(): void {
    this.lowCount = 0;
    this.normalCount = 0;
    this.isLow = false;
  }
}
